import { FC, useContext, useEffect, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePermissionsContext } from "../../context/permissionContext";
import { useI18n } from "../../hooks/useTranslations";
import useProcessingRequestApi from "../../http/processingRequest/api";
import {
  ProcessingRequest,
  ProcessingRequestStatus,
  ProcessingRequestStatuses,
  UpdateRequestMinutes,
} from "../../http/processingRequest/types";
import useProcessingRequestLabelApi from "../../http/processingRequestLabel/api";
import useProcessingRequestLabelsPackagingsApi from "../../http/processingRequestLabelsPackagings/api";
import {
  PackagingAndLabelTypeLabel,
  PackagingAndLabelTypePackaging,
  RequestPackagingAndLabel,
} from "../../http/processingRequestLabelsPackagings/types";
import useProcessingRequestPackagingApi from "../../http/processingRequestPackaging/api";
import { formatMinutes, formatPrice, getHourlyCostFromMinutes } from "../../utils";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import Box from "../ui/Box";
import ContentContainer from "../ui/ContentContainer";
import { Action } from "../ui/ListItemMenu";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import CreateRequestLabelAndPackagingButton from "./CreateRequestLabelAndPackagingButton";
import RequestLabelsAndPackagingsForm, { RequestLabelsAndPackagingsFormBody } from "./RequestLabelAndPackagingForm";
import DeleteLabelsAndPackagingsDialog from "./RequestLabelsAndPackagingsDialog";

type RequestLabelsProps = {
  request: ProcessingRequest;
  requestStatus: ProcessingRequestStatus;
  onUpdate: (trigger: boolean) => void;
};

const RequestLabelsAndPackagings: FC<RequestLabelsProps> = ({ request, requestStatus, onUpdate }) => {
  const i18n = useI18n();
  const [itemToDelete, setItemToDelete] = useState<RequestPackagingAndLabel>();
  const [itemToEdit, setItemToEdit] = useState<RequestPackagingAndLabel>();
  const processingRequestLabelApi = useProcessingRequestLabelApi();
  const processingRequestPackagingApi = useProcessingRequestPackagingApi();
  const processingRequestApi = useProcessingRequestApi();
  const labelsPackagingsApi = useProcessingRequestLabelsPackagingsApi();
  const dispatch = useContext(NotificationDispatch);
  const permissions = usePermissionsContext();

  const { data, error } = labelsPackagingsApi.useProcessingRequestLabelsPackagings(`${request.id}`);

  useEffect(() => {
    onUpdate(false);
  }, [data?.total]);

  const onSubmit = async (data: RequestLabelsAndPackagingsFormBody) => {
    try {
      if (
        [
          ProcessingRequestStatuses.Open,
          ProcessingRequestStatuses.Accepted,
          ProcessingRequestStatuses.WaitingConfirmation,
          ProcessingRequestStatuses.Refused,
          ProcessingRequestStatuses.Discarded,
        ].includes(request.status)
      ) {
        const buildCommon = {
          requestId: request.id,
          unitCost: data.unitCost,
          expectedQuantity: data.quantity,
          usedQuantity: itemToEdit?.usedQuantity ?? 0,
        };
        if (itemToEdit?.type === PackagingAndLabelTypeLabel) {
          await processingRequestLabelApi.updateRequestLabel({
            ...buildCommon,
            label: data.item,
          });
        } else if (itemToEdit?.type === PackagingAndLabelTypePackaging) {
          await processingRequestPackagingApi.updateRequestPackaging({
            ...buildCommon,
            packaging: data.item,
          });
        } else {
          const updateRequest: UpdateRequestMinutes = {
            productionMinutes: request.productionMinutes ?? null,
            expectedMinutes: data.quantity,
            costPerHour: data.unitCost,
          };
          await processingRequestApi.updateRequestMinutes(updateRequest, request.id);
          onUpdate(true);
        }
      } else {
        const buildCommon = {
          requestId: request.id,
          unitCost: data.unitCost,
          expectedQuantity: itemToEdit?.expectedQuantity ?? 0,
          usedQuantity: data.quantity,
        };
        if (itemToEdit?.type === PackagingAndLabelTypeLabel) {
          await processingRequestLabelApi.updateRequestLabel({
            ...buildCommon,
            label: data.item,
          });
        } else if (itemToEdit?.type === PackagingAndLabelTypePackaging) {
          await processingRequestPackagingApi.updateRequestPackaging({
            ...buildCommon,
            packaging: data.item,
          });
        } else {
          const updateRequest: UpdateRequestMinutes = {
            productionMinutes: data.quantity,
            expectedMinutes: request.expectedMinutes ?? null,
            costPerHour: request.costPerHour,
          };
          await processingRequestApi.updateRequestMinutes(updateRequest, request.id);
          onUpdate(true);
        }
      }
      setItemToEdit(undefined);
    } catch (e) {
      dispatch(showErrorNotification(i18n, e));
    }
  };

  const actions: Action<RequestPackagingAndLabel>[] | undefined =
    permissions.isWarehouseOperator &&
    ([
      ProcessingRequestStatuses.Accepted,
      ProcessingRequestStatuses.Refused,
      ProcessingRequestStatuses.InProgress,
    ].includes(requestStatus) ||
      (ProcessingRequestStatuses.Closed == requestStatus && permissions.editClosedRequests))
      ? [
          {
            title: i18n.t.common.edit,
            onClick: setItemToEdit,
            icon: SvgEdit,
          },
          ...(requestStatus !== ProcessingRequestStatuses.InProgress
            ? [
                {
                  title: i18n.t.common.delete,
                  onClick: setItemToDelete,
                  icon: SvgDelete,
                  color: "red" as const,
                  isDivider: true,
                  isDisabled: (item: RequestPackagingAndLabel) => item.code === minutesItem.code,
                },
              ]
            : []),
        ]
      : undefined;

  const minutesItem: RequestPackagingAndLabel = {
    code: i18n.t.common.minutes.toUpperCase(),
    unitCost: request.costPerHour,
    description: i18n.t.common.minutes,
    id: "fakeId",
    expectedQuantity: request.expectedMinutes || 0,
    usedQuantity: request.productionMinutes || 0,
    type: "minutes",
    cost: request.costPerHour,
  };

  const totalExpectedCost =
    data?.items?.reduce((sum, entry) => sum + (entry.unitCost ?? 0) * (entry.expectedQuantity ?? 0), 0) ?? 0;

  const totalActualCost =
    data?.items?.reduce((sum, entry) => sum + (entry.unitCost ?? 0) * (entry.usedQuantity ?? 0), 0) ?? 0;

  return (
    <Box
      type="secondary"
      title={i18n.t.labels.plural + ", " + i18n.t.packagings.plural + " & " + i18n.t.common.workingMinutes}
      isScrollableBody
      fullHeight
    >
      <ContentContainer>
        <Table<RequestPackagingAndLabel>
          buttons={
            permissions.isWarehouseOperator &&
            (requestStatus == ProcessingRequestStatuses.Accepted ||
              requestStatus == ProcessingRequestStatuses.Refused ||
              (requestStatus == ProcessingRequestStatuses.Closed && permissions.editClosedRequests))
              ? {
                  main: <CreateRequestLabelAndPackagingButton requestId={request.id} requestStatus={request.status} />,
                }
              : undefined
          }
          head={
            <tr>
              <th align="left">{i18n.t.common.code}</th>
              <th>
                {i18n.t.common.expectedQuantity}
                {(requestStatus === ProcessingRequestStatuses.Closed ||
                  requestStatus === ProcessingRequestStatuses.OfferApproved ||
                  requestStatus === ProcessingRequestStatuses.InProgress) && <> / {i18n.t.common.usedQuantity}</>}
              </th>
              <th align="right">{i18n.t.common.costPerUnit}</th>
              <th align="right">
                {i18n.t.common.expectedCost}
                {(requestStatus === ProcessingRequestStatuses.Closed ||
                  requestStatus === ProcessingRequestStatuses.OfferApproved ||
                  requestStatus === ProcessingRequestStatuses.InProgress) && <> / {i18n.t.common.actualCost}</>}
              </th>
              {actions?.length && actions?.length > 0 && <th align="left"></th>}
            </tr>
          }
          onRowClick={setItemToEdit}
          renderRow={(item) => (
            <>
              <td align="left">
                {item.code === i18n.t.common.minutes.toUpperCase() ? i18n.t.common.minutes : item.code}
              </td>
              <td align="center">
                {formatMinutes(item.expectedQuantity, permissions.preferredLanguage)}
                {(requestStatus === ProcessingRequestStatuses.Closed ||
                  requestStatus === ProcessingRequestStatuses.OfferApproved ||
                  requestStatus === ProcessingRequestStatuses.InProgress) && (
                  <> / {formatMinutes(item.usedQuantity, permissions.preferredLanguage)}</>
                )}
              </td>
              <td align="right">
                {formatPrice(
                  item.code === i18n.t.common.minutes.toUpperCase() ? item.unitCost / 60 : item.unitCost,
                  permissions.preferredLanguage,
                  {
                    min: 2,
                    max: item?.code === i18n.t.common.minutes.toUpperCase() ? 2 : 4,
                  },
                )}
              </td>
              <td align="right">
                {formatPrice(
                  item.type === "minutes"
                    ? getHourlyCostFromMinutes(item.expectedQuantity, item.unitCost)
                    : item.unitCost * item.expectedQuantity,
                  permissions.preferredLanguage,
                  {
                    min: 2,
                    max: item?.code === i18n.t.common.minutes.toUpperCase() ? 2 : 4,
                  },
                )}

                {(requestStatus === ProcessingRequestStatuses.Closed ||
                  requestStatus === ProcessingRequestStatuses.OfferApproved ||
                  requestStatus === ProcessingRequestStatuses.InProgress) && (
                  <>
                    {" "}
                    /{" "}
                    {formatPrice(
                      item.type === "minutes"
                        ? getHourlyCostFromMinutes(item.usedQuantity, item.unitCost)
                        : item.unitCost * item.usedQuantity,
                      permissions.preferredLanguage,
                      {
                        min: 2,
                        max: item?.code === i18n.t.common.minutes.toUpperCase() ? 2 : 4,
                      },
                    )}
                  </>
                )}
              </td>
            </>
          )}
          footer={
            <tr>
              <td colSpan={2}>{i18n.t.common.totalCost}:</td>
              <td colSpan={2} align="right">
                {formatPrice(
                  totalExpectedCost + getHourlyCostFromMinutes(minutesItem.expectedQuantity, minutesItem.unitCost),
                  permissions.preferredLanguage,
                  {
                    min: 2,
                    max: 4,
                  },
                )}
                {(requestStatus === ProcessingRequestStatuses.Closed ||
                  requestStatus === ProcessingRequestStatuses.OfferApproved ||
                  requestStatus === ProcessingRequestStatuses.InProgress) && (
                  <>
                    {" "}
                    /{" "}
                    {formatPrice(
                      totalActualCost + getHourlyCostFromMinutes(minutesItem.usedQuantity, minutesItem.unitCost),
                      permissions.preferredLanguage,
                      {
                        min: 2,
                        max: 4,
                      },
                    )}
                  </>
                )}
              </td>
            </tr>
          }
          rowActions={actions}
          items={[...(data?.items ?? []), minutesItem]}
          error={error}
        />
        {!!itemToDelete && (
          <DeleteLabelsAndPackagingsDialog
            requestId={request.id}
            item={itemToDelete}
            onClose={() => setItemToDelete(undefined)}
          />
        )}
        <Sidebar
          isOpen={!!itemToEdit}
          onClose={() => setItemToEdit(undefined)}
          title={
            itemToEdit?.type == PackagingAndLabelTypeLabel
              ? i18n.t.labels.editLabel
              : itemToEdit?.type == PackagingAndLabelTypePackaging
                ? i18n.t.packagings.editPackaging
                : i18n.t.common.editMinutes
          }
        >
          {itemToEdit && (
            <RequestLabelsAndPackagingsForm requestStatus={request.status} item={itemToEdit} onSubmit={onSubmit} />
          )}
        </Sidebar>
      </ContentContainer>
    </Box>
  );
};

export default RequestLabelsAndPackagings;
